import React from 'react';
import { Icon } from './icons';

const cx = (...parts) => parts.filter(Boolean).join(' ');

export const Button = ({ variant, size, icon, loading, children, className, ...rest }) => (
  <button type="button" className={cx('btn', variant, size, !children && 'icon', className)}
    disabled={loading || rest.disabled} {...rest}>
    {loading ? <span className="spinner" /> : icon && <Icon name={icon} size={16} />}
    {children}
  </button>
);

export const Field = ({ label, hint, error, children, className }) => (
  <div className={cx('field', className)}>
    {label && <label>{label}</label>}
    {children}
    {error ? <span className="err" role="alert">{error}</span> : hint && <span className="hint">{hint}</span>}
  </div>
);

export const Input = ({ invalid, className, ...rest }) => <input className={cx('input', invalid && 'invalid', className)} aria-invalid={invalid || undefined} {...rest} />;

export const Textarea = ({ invalid, className, ...rest }) => <textarea className={cx('textarea', invalid && 'invalid', className)} aria-invalid={invalid || undefined} {...rest} />;

export const Select = ({ options = [], placeholder, invalid, className, ...rest }) => (
  <select className={cx('select', invalid && 'invalid', className)} aria-invalid={invalid || undefined} {...rest}>
    {placeholder !== undefined && <option value="">{placeholder}</option>}
    {options.map((o) => {
      const opt = typeof o === 'object' ? o : { value: o, label: o };
      return <option key={opt.value} value={opt.value}>{opt.label}</option>;
    })}
  </select>
);

export const SearchBox = ({ value, onChange, placeholder = 'Search…' }) => (
  <div className="search"><Icon name="search" size={16} />
    <input className="input" value={value || ''} placeholder={placeholder} onChange={(e) => onChange(e.target.value)} /></div>
);

export const Card = ({ title, subtitle, actions, flush, className, children }) => (
  <section className={cx('card', flush && 'flush', className)}>
    {(title || actions) && (
      <div className="card-head">
        <div className="grow">{title && <h2>{title}</h2>}{subtitle && <p>{subtitle}</p>}</div>
        {actions}
      </div>
    )}
    <div className="card-body">{children}</div>
  </section>
);

/** "What this is for" panel shown at the top of an analytic, so a first-time user knows why it exists and how to read it. */
export const Purpose = ({ what, use, care }) => (
  <details className="purpose" open>
    <summary>What this is for</summary>
    <p>{what}</p>
    {use && <p><b>How to use it:</b> {use}</p>}
    {care && <p><b>Keep in mind:</b> {care}</p>}
  </details>
);

export const Stat = ({ label, value, sub, onClick }) => (
  <div className={cx('card', 'stat', onClick && 'clickable')} onClick={onClick}
    role={onClick ? 'button' : undefined} tabIndex={onClick ? 0 : undefined}
    onKeyDown={onClick ? (e) => e.key === 'Enter' && onClick() : undefined}>
    <span className="label">{label}</span><span className="value">{value}</span>{sub && <span className="sub">{sub}</span>}
  </div>
);

export const Badge = ({ tone, children }) => <span className={cx('badge', tone)}>{children}</span>;
export const Progress = ({ value, max }) => (
  <div className="progress"><i style={{ width: `${max ? Math.min(100, (value / max) * 100) : 0}%` }} /></div>
);
export const Alert = ({ tone, children }) => <div className={cx('alert', tone)} role="alert">{children}</div>;
export const Spinner = () => <span className="spinner" />;
export const Skeleton = ({ w = '100%', h = 14 }) => <div className="skeleton" style={{ width: w, height: h }} />;

export const Empty = ({ title = 'Nothing here yet', children }) => (
  <div className="empty"><Icon name="inbox" size={36} /><strong>{title}</strong>{children}</div>
);

export const Tabs = ({ tabs, value, onChange }) => (
  <div className="tabs" role="tablist">
    {tabs.map((t) => (
      <button key={t.key} role="tab" aria-selected={value === t.key} className={value === t.key ? 'active' : ''}
        onClick={() => onChange(t.key)}>{t.label}</button>
    ))}
  </div>
);

export const Segmented = ({ options, value, onChange }) => (
  <div className="seg">
    {options.map((o) => (
      <button key={o.value} className={value === o.value ? 'on' : ''} onClick={() => onChange(o.value)}>{o.label}</button>
    ))}
  </div>
);

export const Chip = ({ on, children, ...rest }) => <button type="button" className={cx('chip', on && 'on')} {...rest}>{children}</button>;
export const Check = ({ label, ...rest }) => <label className="check"><input type="checkbox" {...rest} />{label}</label>;
